import { AdService } from '..'
import { Store } from '../../../utils/storage/keys'
import { LocalStore } from '../../../utils/storage/local'
import { PageType } from '../../common/page-types'
import { AdTargetLookup } from './lookup'
import { IAdTargetStrategyHandler } from './strategy'
import { AdTarget, AdTargets, IKeyValues } from './target'

interface IWindow {
    I10C: any
}

declare let window: IWindow

/**
 * Targeting keys as defined by Ad Ops. Please be vigilant in minimizing additions (be aware of redundancy etc.)
 * DO NOT repeat these key definitions elsewhere (extract if multiple targeters are required).
 */
export enum AdTargetKeys {
    ARTICLE = 'aid',
    AUTHOR = 'author',
    CHANNEL = 'ck',
    COMMUNITY = 'comm',
    ENTITY = 'et',
    IMPRESSION = 'imp',
    LOCATION = 'loc',
    NETWORK = 'nk',
    PAGE = 'page',
    PARTNER = 'prtn',
    OPT_OUT_POL = 'pol',
    PROPERTY = 'pr',
    REFERRER = 'r',
    SHIELD = 'shield',
    SUBCHANNEL = 'sck',
    TAG = 'tag',
    TEST = 'test', // NOTE: should be `yes` unless prod!
    TYPE = 'type'
}

export interface IPageTargetingConfig {
    network?: string
    property: string
    ancestry?: string[]
    articleId?: string
    authors?: string[]
    entity?: string
    originId?: string
    type?: PageType
}

export class DefaultAdTargetingStrategy implements IAdTargetStrategyHandler {
    public default = {
        loc: 'top',
        network: 'print',
        page: PageType.INDEX
    }

    protected lookup: AdTargetLookup
    protected pageTargets: AdTarget[] = []

    constructor(lookup: any) {
        this.lookup = new AdTargetLookup(lookup)
    }

    /**
     * Determines ad-level targeting. Typical use is setting `loc` when multiple of same size.
     * Additional targeting is rarely used, but supported.
     *
     * @param el Ad element
     */
    public ad(el: Element): AdTargets {
        const targets = []

        // set location
        targets.push(new AdTarget(AdTargetKeys.LOCATION,
            el.hasAttribute(AdService.DOM.attrs.loc)
                ? el.getAttribute(AdService.DOM.attrs.loc)
                : this.default.loc))

        // set unknown-key targeting
        const keyValues = JSON.parse(el.getAttribute(AdService.DOM.attrs.target) || 'null') as IKeyValues
        if (keyValues) {
            Object.keys(keyValues).forEach((key) => targets.push(new AdTarget(key, keyValues[key])))
        }

        return targets
    }

    /**
     * Determines page-level targeting.
     */
    public page(conf: IPageTargetingConfig): AdTargets {
        this.pageTargets = []

        // targets using config data
        this.targetArticle(conf.articleId, conf.originId)
        this.targetAuthors(conf.authors)
        this.targetNetwork(conf.network)
        this.targetPageType(conf.type)
        this.targetProperty(conf.property)

        // targets using env/lookup data
        this.targetCommunity()
        this.targetEntity()
        this.targetOptOut()
        this.targetPartner()
        this.targetReferrer()
        this.targetShield()

        // targets using logic based on ancestry and type
        this.targetWithAncestry(conf.ancestry, conf.type)

        // `test` - should be 'yes' unless prod!
        if (__NON_PROD) {
            this.pageTargets.push(new AdTarget(AdTargetKeys.TEST, 'yes'))
        }

        return this.pageTargets
    }

    /**
     * Article - from page conf; array of article and origin ids.
     */
    protected targetArticle(articleId: string, originId: string) {
        if (articleId) {
            this.pageTargets.push(new AdTarget(AdTargetKeys.ARTICLE, [articleId, originId]))
        }
    }

    /**
     * Authors - from page conf; array of author display names.
     */
    protected targetAuthors(authors: string[]) {
        if (authors && authors.length) {
            this.pageTargets.push(new AdTarget(AdTargetKeys.AUTHOR, authors))
        }
    }

    /**
     * Community - lookup by host.
     * @todo: would be nice if this could accept `1` rather than `y`
     */
    protected targetCommunity() {
        const comm = this.lookup.byHost(location.host, AdTargetKeys.COMMUNITY)
        if (comm) {
            this.pageTargets.push(new AdTarget(AdTargetKeys.COMMUNITY, comm))
        }
    }

    /**
     * Entity - lookup by host; e.g. `sm` (Sun Media).
     */
    protected targetEntity() {
        const entity = this.lookup.byHost(location.host, AdTargetKeys.ENTITY)
        if (entity) {
            this.pageTargets.push(new AdTarget(AdTargetKeys.ENTITY, entity))
        }
    }

    /**
     * Network - from page conf; usually `print`; e.g. `ccn`.
     * @todo: add to lookup
     */
    protected targetNetwork(network: string) {
        this.pageTargets.push(new AdTarget(AdTargetKeys.NETWORK, network || this.default.network))
    }

    /**
     * Page - from page conf; e.g. `search`.
     */
    protected targetPageType(type: string) {
        // category pages target using page=index
        if (type === PageType.CATEGORY) {
            this.pageTargets.push(new AdTarget(AdTargetKeys.PAGE, PageType.INDEX))

        } else {
            this.pageTargets.push(new AdTarget(AdTargetKeys.PAGE, type || this.default.page))
        }
    }

    /**
     * Property - from page conf; e.g. `np`.
     * @todo: add to lookup
     * @todo: SHOULD BE DOMAIN NOT LOOKUP
     */
    protected targetProperty(property: string) {
        this.pageTargets.push(new AdTarget(AdTargetKeys.PROPERTY, property))
    }

    /**
     * Partner - lookup by host; e.g. `adperfect`.
     */
    protected targetPartner() {
        const partner = this.lookup.byHost(location.host, AdTargetKeys.PARTNER)
        if (partner) {
            this.pageTargets.push(new AdTarget(AdTargetKeys.PARTNER, partner))
        }
    }

    /**
     * Referrer - fetched from `document`.
     */
    protected targetReferrer() {
        if (document.referrer) {
            const domain = document.referrer.replace(/^.*\/\/([^\/]+).*$/, '$1')
            this.pageTargets.push(new AdTarget(AdTargetKeys.REFERRER, domain))
        }
    }

    /**
     * Shield - for 'Index Exchange' (?)
     * @todo: explain
     */
    protected targetShield() {
        if (window.I10C && window.I10C.Morph) {
            this.pageTargets.push(new AdTarget(AdTargetKeys.SHIELD, `1`))
        }
    }

    /**
     * Handles targeting involving ancestry and type:
     * Channel - usually top-most ancestor; e.g. `news`.
     * Sub-channel - array of ancestor slugs.
     * Impression - usually last-most ancestor; `index` on home pages
     */
    protected targetWithAncestry(ancestry: string[], type: PageType) {

        // has heirarchy (typically categories)
        if (ancestry && ancestry.length) {

            // channel key is top-most ancestor
            this.pageTargets.push(new AdTarget(AdTargetKeys.CHANNEL, ancestry[0]))

            // remaining ancestors are sub-category keys
            if (ancestry.length > 1) {
                this.pageTargets.push(new AdTarget(AdTargetKeys.SUBCHANNEL, ancestry.slice(1)))
            }

            // impression is last-most ancestor
            this.pageTargets.push(new AdTarget(AdTargetKeys.IMPRESSION, [...ancestry].pop()))

        } else {

            // home page has 'index' as ck and imp
            if (type === PageType.INDEX) {
                this.pageTargets.push(new AdTarget(AdTargetKeys.CHANNEL, PageType.INDEX))
                this.pageTargets.push(new AdTarget(AdTargetKeys.IMPRESSION, PageType.INDEX))
            }
        }
    }

    /**
     * OptOut - for 'Opt Out Ads' `pol`
     */
    protected targetOptOut() {
        if (LocalStore.get(Store.OPT_OUT_POL)) {
            this.pageTargets.push(new AdTarget(AdTargetKeys.OPT_OUT_POL, 'true' ))
        }
    }

}
